import { useState, useEffect } from 'react';

// mui
import { Dialog, Box, Stack, Typography, ToggleButtonGroup, ToggleButton, Button, Divider } from '@mui/material';

// icons
import {
    FavoriteBorder as FavoriteBorderIcon,
    VisibilityOutlined as VisibilityOutlinedIcon,
    ChatBubbleOutlineOutlined as ChatBubbleOutlineOutlinedIcon,
} from '@mui/icons-material';

// shared
import { def } from '@goldblack/shared';

// client-shared
import { MarkDown, UserAvatar, UserBadges } from '@goldblack/client-comps';
import { number } from '@goldblack/client-utils';

const POST_AVATAR_CONFIG = { postAvatarSize: 38 };

// api
import { AdminPostChangeStatus, AdminPostVector } from '../../../api';

// local
import { PostShowMediaDialog } from './post-showmedia.jsx';
import { PostShowMediaOverlay } from './post-showmediaoverlay.jsx';

const STATUS_LABELS = {
    [def.PostStatus.ACTIVE]: 'Active',
    [def.PostStatus.INACTIVE]: 'Inactive',
    [def.PostStatus.BLOCKED]: 'Blocked',
    [def.PostStatus.CENSORED]: 'Censored',
};

const iconSx = { fontSize: '28px', color: 'white', mr: 0.5 };
const textSx = { fontSize: '12px', lineHeight: '1', color: 'white' };

function fmtDate(val) {
    if (!val) return '';
    const d = typeof val === 'number' ? new Date(val) : new Date(Date.parse(val));
    return isNaN(d) ? String(val) : d.toLocaleString('ko-KR');
}

function StatItem({ icon, value }) {
    return (
        <Stack direction='row' justifyContent='center' alignItems='center' sx={{ mx: 1 }}>
            {icon}
            <Typography sx={textSx}>{number.formatNumber(value, 1)}</Typography>
        </Stack>
    );
}

function PostVectorPanel({ postVector }) {
    if (postVector === undefined) return null;
    return (
        <Box sx={{ px: 1.5, py: 1 }}>
            <Stack direction='row' alignItems='center' spacing={2}>
                <Typography variant='caption' color='text.secondary'>
                    Post Vector
                </Typography>
                {postVector === null ? (
                    <Typography variant='caption' color='text.disabled'>
                        no vector
                    </Typography>
                ) : (
                    <Typography variant='caption' color='text.secondary'>
                        dim: {postVector.dim} &nbsp; norm: {postVector.norm} &nbsp; created:{' '}
                        {fmtDate(postVector.created_at) || '—'}
                    </Typography>
                )}
            </Stack>
        </Box>
    );
}

function PostViewAdmin({ open, onClose, post, user, onStatusChange }) {
    const [descExpanded, setDescExpanded] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [applying, setApplying] = useState(false);
    const [postVector, setPostVector] = useState(undefined);

    useEffect(() => {
        if (post) {
            // Active is the schema default (posts.status DEFAULT 0), so a missing
            // status means active — show it selected rather than blank.
            setSelectedStatus(post.status != null ? Number(post.status) : def.PostStatus.ACTIVE);
            setPostVector(undefined);
            AdminPostVector(post._id, setPostVector);
        }
    }, [post]);

    if (!post) return null;

    const currentStatus = post.status != null ? Number(post.status) : def.PostStatus.ACTIVE;

    function handleApply() {
        if (selectedStatus === null || selectedStatus === currentStatus) return;
        setApplying(true);
        AdminPostChangeStatus(post._id, selectedStatus, (updatedPost) => {
            setApplying(false);
            onStatusChange?.(updatedPost);
        });
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='sm'
            fullWidth
            slotProps={{ paper: { elevation: 0, variant: 'outlined' } }}>
            <Stack direction='column'>
                {/* top: media */}
                <Box
                    onClick={onClose}
                    sx={{
                        position: 'relative',
                        userSelect: 'none',
                        minHeight: 320,
                        overflow: 'hidden',
                        cursor: 'pointer',
                    }}>
                    {PostShowMediaDialog(post)}
                    {PostShowMediaOverlay(post, true)}

                    {/* header: user info */}
                    <Stack
                        direction='row'
                        alignItems='center'
                        gap={1}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            background: 'linear-gradient(#0000007f, #00000000)',
                            m: 0,
                            p: 1,
                            userSelect: 'none',
                        }}>
                        <UserAvatar variant='post' user={user} config={POST_AVATAR_CONFIG} />
                        <Stack direction='row' alignItems='center' gap={0} sx={{ minWidth: 0 }}>
                            <Typography variant='body2' noWrap sx={{ color: 'white', fontWeight: 'bold' }}>
                                {user?.username}
                            </Typography>
                            <UserBadges badges={user?.badges} />
                        </Stack>
                    </Stack>

                    {/* bottom: stats + desc */}
                    <Box
                        onClick={(e) => {
                            e.stopPropagation();
                            setDescExpanded((v) => !v);
                        }}
                        sx={{
                            width: '100%',
                            maxHeight: descExpanded ? '50%' : 'auto',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            background: 'linear-gradient(#00000000, #0000007f)',
                            overflow: descExpanded ? 'auto' : 'hidden',
                            userSelect: 'text',
                            cursor: 'default',
                            '&::-webkit-scrollbar': { width: 0 },
                        }}>
                        <Stack direction='row' justifyContent='flex-start' alignItems='center'>
                            <StatItem icon={<FavoriteBorderIcon sx={iconSx} />} value={post.likes} />
                            <StatItem icon={<ChatBubbleOutlineOutlinedIcon sx={iconSx} />} value={post.comments} />
                            <StatItem icon={<VisibilityOutlinedIcon sx={iconSx} />} value={post.views} />
                        </Stack>
                        {post.desc && <MarkDown src={post.desc} color='text.primary' />}
                    </Box>
                </Box>

                <Divider />

                {/* bottom: status + vector */}
                <Stack direction='column'>
                    <Stack
                        direction='row'
                        alignItems='center'
                        spacing={1}
                        sx={{ px: 1.5, py: 1, flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant='caption' color='text.secondary'>
                            Post Status
                        </Typography>
                        <ToggleButtonGroup
                            exclusive
                            orientation='horizontal'
                            value={selectedStatus}
                            onChange={(_, v) => {
                                if (v !== null) setSelectedStatus(v);
                            }}
                            size='small'>
                            {Object.entries(STATUS_LABELS).map(([val, label]) => (
                                <ToggleButton key={val} value={Number(val)} sx={{ fontSize: 11, py: 0.5, px: 1 }}>
                                    {label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        <Button
                            variant='contained'
                            size='small'
                            disabled={applying || selectedStatus === currentStatus}
                            onClick={handleApply}
                            sx={{ ml: 'auto' }}>
                            Apply
                        </Button>
                    </Stack>

                    <Divider />

                    <PostVectorPanel postVector={postVector} />
                </Stack>
            </Stack>
        </Dialog>
    );
}

export { PostViewAdmin };
